'use client';

import type { Permission } from '@up2stay/up2promo-api-client';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Switch,
} from '@up2stay/up2promo-ui';
import { ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  CUSTOM_ROLE_BASELINE_PERMISSIONS,
  CUSTOM_ROLE_FORBIDDEN_PERMISSIONS,
  groupPermissionsByModule,
  PERMISSION_METADATA,
  PERMISSION_MODULES,
  type PermissionMetadata,
  type PermissionModule,
} from '@/core/authz/permission-catalog';
import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';

type PermissionLabels = {
  modules: Partial<Record<PermissionModule, string>>;
  getPermissionLabel: (labelKey: string) => string;
};

interface PermissionMatrixProps {
  value: Permission[];
  onChange: (permissions: Permission[]) => void;
  canAssign: (permission: Permission) => boolean;
  labels: PermissionLabels;
  readOnly?: boolean;
  searchPlaceholder?: string;
  alwaysGrantedLabel?: string;
}

function isBaselinePermission(permission: Permission): boolean {
  return CUSTOM_ROLE_BASELINE_PERMISSIONS.includes(permission);
}

function getNestedLabel(
  dict: Record<string, unknown>,
  path: string,
): string | undefined {
  const parts = path.split('.');
  let current: unknown = dict;
  for (const part of parts) {
    if (!current || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

export function createPermissionLabels(
  permissionsDict: Record<string, unknown>,
): PermissionLabels {
  const modules =
    (permissionsDict.modules as Partial<Record<PermissionModule, string>>) ??
    {};

  return {
    modules,
    getPermissionLabel: (labelKey: string) => {
      const path = labelKey.startsWith('permissions.')
        ? labelKey.slice('permissions.'.length)
        : labelKey;
      const nested = getNestedLabel(permissionsDict, path);
      if (nested) return nested;
      return path.split('.').pop() ?? labelKey;
    },
  };
}

export function PermissionMatrix({
  value,
  onChange,
  canAssign,
  labels,
  readOnly = false,
  searchPlaceholder = 'Search permissions…',
  alwaysGrantedLabel,
}: PermissionMatrixProps) {
  const [search, setSearch] = useState('');
  const selected = useMemo(
    () => new Set([...CUSTOM_ROLE_BASELINE_PERMISSIONS, ...value]),
    [value],
  );

  // Read-only views list every permission: the editor filter hid
  // workspace.delete, so Eigentümer looked like Administrator (DEV-538).
  const assignableMetadata = useMemo(
    () =>
      readOnly
        ? PERMISSION_METADATA
        : PERMISSION_METADATA.filter(
            (entry) =>
              canAssign(entry.permission) &&
              !CUSTOM_ROLE_FORBIDDEN_PERMISSIONS.includes(entry.permission),
          ),
    [canAssign, readOnly],
  );

  const filteredBySearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return assignableMetadata;
    return assignableMetadata.filter((entry) => {
      const label = labels.getPermissionLabel(entry.labelKey).toLowerCase();
      const moduleLabel = (
        labels.modules[entry.module] ?? entry.module
      ).toLowerCase();
      return (
        label.includes(q) ||
        moduleLabel.includes(q) ||
        entry.permission.toLowerCase().includes(q)
      );
    });
  }, [assignableMetadata, labels, search]);

  const grouped = useMemo(
    () => groupPermissionsByModule(filteredBySearch),
    [filteredBySearch],
  );

  const togglePermission = (permission: Permission, checked: boolean) => {
    if (readOnly || isBaselinePermission(permission)) return;
    const next = new Set(selected);
    if (checked) {
      next.add(permission);
    } else {
      next.delete(permission);
    }
    onChange([...next]);
  };

  const toggleModule = (entries: PermissionMetadata[], selectAll: boolean) => {
    if (readOnly) return;
    const next = new Set(selected);
    for (const entry of entries) {
      if (isBaselinePermission(entry.permission)) continue;
      if (selectAll) {
        next.add(entry.permission);
      } else {
        next.delete(entry.permission);
      }
    }
    onChange([...next]);
  };

  const moduleOrder = PERMISSION_MODULES.filter((m) => grouped.has(m));

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="pl-9"
          disabled={readOnly}
        />
      </div>

      <div className="flex flex-col gap-3">
        {moduleOrder.map((module) => {
          const entries = grouped.get(module) ?? [];
          if (entries.length === 0) return null;

          const moduleLabel = labels.modules[module] ?? module;
          const allSelected = entries.every((e) => selected.has(e.permission));
          const someSelected = entries.some((e) => selected.has(e.permission));
          const allBaseline = entries.every((e) =>
            isBaselinePermission(e.permission),
          );

          return (
            <Collapsible key={module} defaultOpen>
              <SettingsGroup>
                <SettingsGroupRows>
                  <SettingsGroupRow>
                    <div className="flex items-center gap-2">
                      <CollapsibleTrigger className="flex min-w-0 flex-1 items-center justify-between text-left text-[13.5px] font-semibold leading-snug text-foreground">
                        <span className="truncate">{moduleLabel}</span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </CollapsibleTrigger>
                      <PermissionSwitchRow
                        id={`module-${module}`}
                        label={moduleLabel}
                        hideLabel
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        disabled={readOnly || allBaseline}
                        onCheckedChange={(checked) =>
                          toggleModule(entries, checked)
                        }
                      />
                    </div>
                  </SettingsGroupRow>
                </SettingsGroupRows>
                <CollapsibleContent>
                  <SettingsGroupRows>
                    {entries.map((entry) => {
                      const locked = isBaselinePermission(entry.permission);
                      return (
                        <SettingsGroupRow key={entry.permission} separated>
                          <PermissionSwitchRow
                            id={entry.permission}
                            label={labels.getPermissionLabel(entry.labelKey)}
                            hint={
                              locked && !readOnly
                                ? alwaysGrantedLabel
                                : undefined
                            }
                            checked={selected.has(entry.permission)}
                            subdued
                            disabled={readOnly || locked}
                            onCheckedChange={(checked) =>
                              togglePermission(entry.permission, checked)
                            }
                          />
                        </SettingsGroupRow>
                      );
                    })}
                  </SettingsGroupRows>
                </CollapsibleContent>
              </SettingsGroup>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
}

function PermissionSwitchRow({
  id,
  label,
  hint,
  checked,
  indeterminate,
  subdued,
  hideLabel,
  disabled,
  onCheckedChange,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  indeterminate?: boolean;
  subdued?: boolean;
  hideLabel?: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div
      className={
        hideLabel ? 'shrink-0' : 'flex items-center justify-between gap-3'
      }
    >
      {!hideLabel ? (
        <label
          htmlFor={id}
          className={
            subdued
              ? 'min-w-0 flex-1 cursor-pointer text-[13.5px] leading-snug text-muted-foreground'
              : 'min-w-0 flex-1 cursor-pointer text-[13.5px] font-medium leading-snug'
          }
        >
          {label}
          {hint ? (
            <span className="ml-1.5 text-[11.5px] uppercase tracking-wide text-muted-foreground/70">
              {hint}
            </span>
          ) : null}
        </label>
      ) : null}
      <Switch
        id={id}
        checked={indeterminate ? false : checked}
        aria-checked={indeterminate ? 'mixed' : checked}
        disabled={disabled}
        aria-label={hideLabel ? label : undefined}
        className={
          indeterminate
            ? 'bg-primary/45 data-[state=unchecked]:bg-primary/45 [&>span]:translate-x-2.5'
            : undefined
        }
        onCheckedChange={(value) => {
          if (indeterminate) {
            onCheckedChange(true);
            return;
          }
          onCheckedChange(value);
        }}
      />
    </div>
  );
}
